import {Injectable} from "@angular/core";
import {environment} from "../../../environments/environment";
import {HttpClient} from "@angular/common/http";
import {JwtToken} from "../model/jwt-token.model";
import {RegisterRequest} from "../model/register-request.model";
import {UserInfo} from "../model/user-info.model";
import {map} from "rxjs/operators";
import {BehaviorSubject, Subject, switchMap} from "rxjs";
import {SocialLoginRequestDTO} from "../model/social-login-request.model";
import {NavigationService} from "./navigation.service";
import {UserPreference} from "../model/user-preference.model";
import {v4 as uuidv4} from 'uuid';
import {newsCategoryType, userRoles} from "../types/types";

@Injectable({
  providedIn: 'root'
})
export class UserService {

  private localUserInfo: string = 'user-info';
  private localKeyUUID: string = 'onesignal-uuid';
  private _baseUrl: string = environment.host + "/users";
  private _tokenStorageKey: string = 'token';
  private _user: UserInfo;
  private _username: string;
  private _roles: userRoles[];

  changeNewsCategories = new Subject<newsCategoryType[]>;

  constructor(private http: HttpClient,
              private navigationService: NavigationService) {

    this._user = this.getUserInfoFromLocalStorage();
  }

  get roles() {
    return this._roles;
  }

  set roles(roles: userRoles[]) {
    this._roles = roles;
  }

  get username() {
    return this._username;
  }

  set username(username: string) {
    this._username = username;
  }

  get token(): JwtToken {
    const localValue = localStorage.getItem(this._tokenStorageKey);
    return localValue ? JSON.parse(localValue) : undefined;
  }

  set token(token: JwtToken) {
    if (token) {
      localStorage.setItem(this._tokenStorageKey, JSON.stringify(token));
    } else {
      localStorage.removeItem(this._tokenStorageKey);
    }
  }

  get uuid() {
    return localStorage.getItem(this.localKeyUUID);
  }

  set uuid(uuid: string) {
    localStorage.setItem(this.localKeyUUID, uuid);
  }

  isAdmin(){
    return this._roles?.includes('ADMIN');
  }

  generateGuestUser() {

    this.uuid = uuidv4();
    this.user = {
      isGuest: true,
      preference: {
        hasFavoriteEvents: false,
        newsCategories: []
      }
    };
  }

  set newsCategories(categories: newsCategoryType[]) {
    this._user.preference.newsCategories = categories;
    this.user = this._user;
    this.changeNewsCategories.next(categories);
  }

  get user() {
    return this._user;
  }

  set user(user) {
    this._user = user;
    localStorage.setItem(this.localUserInfo, JSON.stringify(user));
  }

  deleteProfileMe() {
    return this.http.delete(`${this._baseUrl}/${this.user.id}`, {});
  }

  updateProfileById(user: UserInfo) {
    return this.http.put(`${this._baseUrl}/${this.user.id}`, user);
  }

  resendConfirmationMail() {
    return this.http.post(environment.host + "/users/mail/resend/" + this.username, {});
  }

  confirmMail(token: string) {
    return this.http.post(environment.host + "/users/mail/confirm/" + token, {});
  }

  login(username: string, password: string) {
    return this.http.post<JwtToken>(this._baseUrl + "/login", {
      username,
      password
    }).pipe(
      map((res) => {
        this.token = res;
        return res;
      }),
      switchMap(() => {
        return this.getProfileMe();
      })
    );
  }

  logout() {
    this._user = null;
    this.token = undefined;
  }

  registerSocial(request: SocialLoginRequestDTO) {
    return this.http.post<JwtToken>(this._baseUrl + "/register/social", request).pipe(
      map((res) => {
        this.token = res;
        return res;
      }),
      switchMap(() => {
        return this.getProfileMe();
      })
    );
  }

  register(request: RegisterRequest) {
    return this.http.post<UserInfo>(this._baseUrl + "/register", request);
  }

  resetPwd(username: string) {
    return this.http.post<JwtToken>(this._baseUrl + "/pwd/reset", {
      username
    });
  }

  changePwd(password: string, resetToken: string) {
    return this.http.post<JwtToken>(this._baseUrl + "/pwd/change", {
      password,
      resetToken
    });
  }

  getProfileMe() {
    return this.http.get<UserInfo>(`${this._baseUrl}/me`).pipe(
      switchMap((user) =>
        this.getProfilePreference().pipe(
          map((prefs) => {

            user.preference = prefs;
            this.restoreProfilePreference(user);

            this.user = user;
            return user;
          })
        )
      )
    );
  }

  getProfilePreference() {
    return this.http.get<UserPreference>(this._baseUrl + "/me/preferences");
  }

  async showLoginModal() {
    this.navigationService.openPagesModal('login');
  }

  private restoreProfilePreference(user: UserInfo) {
    user.preference.newsCategories = this.getUserInfoFromLocalStorage()?.preference?.newsCategories || [];
  }


  private getUserInfoFromLocalStorage(): UserInfo {

    if (localStorage.getItem(this.localUserInfo)) {
      return JSON.parse(localStorage.getItem(this.localUserInfo));
    } else {
      return undefined;
    }
  }

}
